import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetBanquetBookingsMutation,
  useDeleteBanquetBookingMutation,
  useSaveBanquetBookingMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import ReactToPrint from "react-to-print";
import html2pdf from "html2pdf.js";
import BookingPrintView from "./BookingPrintView";
import FinancialBreakdown from "./FinancialBreakdown";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtCAD = (v: number | string | null | undefined): string => {
  const n = Number(v ?? 0);
  return n.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
};

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return "—";
  const d = new Date(v + (v.includes("T") ? "" : "T12:00:00"));
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
};

const TIME_SLOT_LABELS: Record<string, string> = {
  AM:       "AM  (8 am – 1 pm)",
  PM:       "PM  (5 pm – 11 pm)",
  FULL_DAY: "Full Day  (8 am – 11 pm)",
};

const STATUS_OPTIONS = ["quote", "order", "invoice", "complete", "cancel"] as const;
type Status = (typeof STATUS_OPTIONS)[number];

const STATUS_COLOR: Record<Status, "warning" | "success" | "info" | "primary" | "error"> = {
  quote:    "warning",
  order:    "success",
  invoice:  "info",
  complete: "primary",
  cancel:   "error",
};

// ── Info row helper ────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: "flex", gap: 1, py: "5px", borderBottom: "1px solid", borderColor: "divider" }}>
    <Typography variant="caption" sx={{ width: 160, flexShrink: 0, color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

// ── Section header ─────────────────────────────────────────────────────────────
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
    {children}
  </Typography>
);

// ── Component ──────────────────────────────────────────────────────────────────
const BanquetBookingDetails = () => {
  const { id }    = useParams<{ id: string }>();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));
  const classes   = useStyles();
  const navigate  = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [booking,    setBooking]    = useState<any>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const [isPdfing, setIsPdfing] = useState(false);

  const handleDownloadPdf = async () => {
    if (!printRef.current || !booking) return;
    setIsPdfing(true);
    try {
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `${booking.bookingRef || `Booking-${booking.id}`}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      }).from(printRef.current).save();
    } catch { /* silently fail */ }
    setIsPdfing(false);
  };

  const [getBookings]                              = useGetBanquetBookingsMutation();
  const [deleteBooking, { isLoading: isDeleting }] = useDeleteBanquetBookingMutation();
  const [saveBooking]                              = useSaveBanquetBookingMutation();

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchBooking = useCallback(async () => {
    if (!id) return;
    setIsFetching(true);
    try {
      const res = (await (getBookings as any)({ id: Number(id) }).unwrap()) as any;
      if (res.status && Array.isArray(res.data) && res.data.length > 0) {
        setBooking(res.data[0]);
      } else {
        showErrorMessage("Booking not found");
        navigate("/banquet/bookings");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to load booking");
      navigate("/banquet/bookings");
    } finally {
      setIsFetching(false);
    }
  }, [id, getBookings, showErrorMessage, navigate]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  // ── Status change ──────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus: Status) => {
    setStatusAnchor(null);
    if (!booking || newStatus === booking.status) return;
    setIsChangingStatus(true);
    try {
      const payload = {
        id:        booking.id,
        status:    newStatus,
        eventType: booking.eventType,
        eventDate: booking.eventDate?.split("T")[0] ?? booking.eventDate,
        timeSlot:  booking.timeSlot,
        guestCount: booking.guestCount,
        halls: (booking.halls ?? []).map((h: any) => ({
          hallKey:      h.hallKey,
          hallLabel:    h.hallLabel,
          hallCapacity: h.hallCapacity,
        })),
      };
      const res = (await (saveBooking as any)(payload).unwrap()) as any;
      if (res.status) {
        showMessage(`Status changed to ${newStatus}`);
        setBooking((prev: any) => ({ ...prev, status: newStatus }));
      } else {
        showErrorMessage(res.message ?? "Failed to update status");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to update status");
    } finally {
      setIsChangingStatus(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!booking) return;
    try {
      const res = (await deleteBooking(booking.id).unwrap()) as any;
      if (res.status) {
        showMessage("Booking deleted");
        navigate("/banquet/bookings");
      } else {
        showErrorMessage(res.message ?? "Failed to delete");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to delete");
    } finally {
      setDeleteOpen(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const status      = (booking?.status ?? "quote") as Status;
  const statusColor = STATUS_COLOR[status] ?? "default";
  const hallList    = (booking?.halls ?? []).map((h: any) => h.hallLabel).join(", ") || "—";
  const primaryCust = booking?.primaryCustomer;
  const secondCust  = booking?.secondaryCustomer;
  const payments    = booking?.payments ?? [];
  const addons      = booking?.addons   ?? [];

  const paidToDate  = payments.reduce((s: number, p: any) => s + Number(p.amount ?? 0), 0);
  const balance     = Number(booking?.grandTotal ?? 0) - paidToDate - Number(booking?.securityDepositAmount ?? 0);

  if (isFetching) {
    return (
      <Container maxWidth="xl" disableGutters={isMobile} sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
        <LinearProgress sx={{ mt: 2 }} />
      </Container>
    );
  }

  if (!booking) return null;

  return (
    <Container maxWidth="xl" disableGutters={isMobile} sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
      <Box sx={{ pb: { xs: 10, sm: 12 } }}>

        {/* ── Page heading ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon sx={{ width: { xs: 22, sm: 26 }, height: { xs: 22, sm: 26 }, color: "text.primary" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 900, fontSize: { xs: 16, sm: 18, md: 20 }, color: "text.primary" }}>
              {booking.bookingRef ?? `Booking #${booking.id}`}
            </Typography>
            <Chip
              label={status.charAt(0).toUpperCase() + status.slice(1)}
              color={statusColor as any}
              size="small"
              sx={{ fontWeight: 700, fontSize: 12 }}
            />
            {isChangingStatus && <CircularProgress size={16} />}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              className={classes.addButton}
              onClick={() => navigate("/banquet/bookings")}
            >
              Back
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.addButton}
              endIcon={<ExpandMoreIcon />}
              onClick={(e) => setStatusAnchor(e.currentTarget)}
              disabled={isChangingStatus}
            >
              Status
            </Button>
            <ReactToPrint
              trigger={() => (
                <Button size="small" variant="outlined" startIcon={<PrintOutlinedIcon />} className={classes.addButton}>
                  Print
                </Button>
              )}
              content={() => printRef.current}
            />
            <Button size="small" variant="outlined" startIcon={isPdfing ? <CircularProgress size={14} /> : <PictureAsPdfOutlinedIcon />}
              className={classes.addButton} onClick={handleDownloadPdf} disabled={isPdfing}>
              {isPdfing ? "Generating..." : "PDF"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              className={classes.addButton}
              onClick={() => navigate(`/banquet/booking/edit/${booking.id}`)}
            >
              Edit
            </Button>
            <Tooltip title="Delete booking">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteOpen(true)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Status change menu */}
        <Menu
          anchorEl={statusAnchor}
          open={Boolean(statusAnchor)}
          onClose={() => setStatusAnchor(null)}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem
              key={s}
              selected={s === status}
              onClick={() => handleStatusChange(s)}
              sx={{ fontSize: 13, minWidth: 130 }}
            >
              <Chip
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                color={(STATUS_COLOR[s] ?? "default") as any}
                size="small"
                sx={{ fontSize: 11, pointerEvents: "none" }}
              />
            </MenuItem>
          ))}
        </Menu>

        <Grid container spacing={2}>

          {/* ── Left column (2/3) ── */}
          <Grid item xs={12} md={8}>

            {/* ── Event Overview ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <SectionTitle>Event Overview</SectionTitle>
              <InfoRow label="Banquet / Event Name" value={booking.banquetName} />
              <InfoRow label="Booking Reference"    value={booking.bookingRef} />
              <InfoRow label="Event Type"           value={booking.eventType} />
              <InfoRow label="Event Date"           value={fmtDate(booking.eventDate)} />
              <InfoRow label="Time Slot"            value={TIME_SLOT_LABELS[booking.timeSlot] ?? booking.timeSlot} />
              <InfoRow label="Hall(s)"              value={hallList} />
              <InfoRow label="Agent / Coordinator"  value={booking.agentName} />
              <InfoRow label="Guests"               value={
                <Box component="span">
                  {booking.guestCount} adults
                  {Number(booking.childrenCount) > 0 && ` + ${booking.childrenCount} children`}
                  {Number(booking.guestMinimum) > 0  && ` (min ${booking.guestMinimum})`}
                </Box>
              } />
            </Paper>

            {/* ── Customers ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <SectionTitle>Customer Information</SectionTitle>
              <Grid container spacing={2}>
                {/* Primary */}
                <Grid item xs={12} sm={secondCust ? 6 : 12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", display: "block", mb: 1, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em" }}>
                    Primary
                  </Typography>
                  {primaryCust ? (
                    <>
                      <InfoRow label="Name"         value={[primaryCust.firstName, primaryCust.lastName].filter(Boolean).join(" ")} />
                      <InfoRow label="Organization" value={primaryCust.organization} />
                      <InfoRow label="Email"        value={[primaryCust.email1, primaryCust.email2].filter(Boolean).join(" / ")} />
                      <InfoRow label="Phone"        value={[
                        primaryCust.phone1 && `${primaryCust.phone1}${primaryCust.phone1Who ? ` (${primaryCust.phone1Who})` : ""}`,
                        primaryCust.phone2 && `${primaryCust.phone2}${primaryCust.phone2Who ? ` (${primaryCust.phone2Who})` : ""}`,
                      ].filter(Boolean).join(" / ")} />
                      <InfoRow label="Address" value={[primaryCust.address, primaryCust.city, primaryCust.province, primaryCust.postalCode].filter(Boolean).join(", ")} />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No primary customer on record.</Typography>
                  )}
                </Grid>

                {/* Secondary */}
                {secondCust && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 1, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em" }}>
                      Secondary
                    </Typography>
                    <InfoRow label="Name"         value={[secondCust.firstName, secondCust.lastName].filter(Boolean).join(" ")} />
                    <InfoRow label="Organization" value={secondCust.organization} />
                    <InfoRow label="Email"        value={[secondCust.email1, secondCust.email2].filter(Boolean).join(" / ")} />
                    <InfoRow label="Phone"        value={[
                      secondCust.phone1 && `${secondCust.phone1}${secondCust.phone1Who ? ` (${secondCust.phone1Who})` : ""}`,
                      secondCust.phone2 && `${secondCust.phone2}${secondCust.phone2Who ? ` (${secondCust.phone2Who})` : ""}`,
                    ].filter(Boolean).join(" / ")} />
                    <InfoRow label="Address" value={[secondCust.address, secondCust.city, secondCust.province, secondCust.postalCode].filter(Boolean).join(", ")} />
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* ── Services ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <SectionTitle>Services</SectionTitle>

              {/* Hall */}
              <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em", display: "block", mb: 0.5 }}>
                Hall Rental
              </Typography>
              <InfoRow label="Flat Fee"        value={fmtCAD(booking.hallFlatFee)} />
              <InfoRow label="Per Person Rate" value={fmtCAD(booking.hallPricePerPerson)} />

              <Divider sx={{ my: 1.5 }} />

              {/* Food */}
              <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em", display: "block", mb: 0.5 }}>
                Food Service
              </Typography>
              <InfoRow label="Service Option"  value={booking.foodServiceOption} />
              <InfoRow label="Rate (/person)"  value={Number(booking.foodServicePricePerPerson) ? fmtCAD(booking.foodServicePricePerPerson) : "—"} />
              <InfoRow label="Menu Option"     value={booking.menuOption} />
              <InfoRow label="Menu Rate (/pp)" value={Number(booking.menuPricePerPerson) ? fmtCAD(booking.menuPricePerPerson) : "—"} />

              <Divider sx={{ my: 1.5 }} />

              {/* Bar */}
              <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em", display: "block", mb: 0.5 }}>
                Bar Service
              </Typography>
              <InfoRow label="Bar Option"         value={booking.barServiceOption} />
              <InfoRow label="Bar Amount"         value={Number(booking.barServiceAmount) ? fmtCAD(booking.barServiceAmount) : "—"} />
              <InfoRow label="Bar Type"           value={booking.barServiceType} />
              <InfoRow label="Bartender Count"    value={Number(booking.bartenderCount) ? `${booking.bartenderCount} × ${fmtCAD(booking.bartenderAmount)}` : "—"} />
              <InfoRow label="Bar Package"        value={booking.barPackageOption} />
              <InfoRow label="Package Rate (/pp)" value={Number(booking.barPackagePricePerPerson) ? fmtCAD(booking.barPackagePricePerPerson) : "—"} />
              <InfoRow label="Bar Patrons"        value={Number(booking.barPatrons) ? booking.barPatrons : "—"} />
              <InfoRow label="Bar Hours"          value={Number(booking.barHours) ? `${booking.barHours} hrs` : "—"} />
              <InfoRow label="LCBO Licence"       value={booking.lcboLicence} />
            </Paper>

            {/* ── Add-ons ── */}
            {addons.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <SectionTitle>Add-ons</SectionTitle>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12 } }}>
                        <TableCell>Item</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {addons.map((a: any, i: number) => (
                        <TableRow key={i} sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell sx={{ fontSize: 12 }}>{a.itemName}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{a.category}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>{fmtCAD(a.unitPrice)}</TableCell>
                          <TableCell align="center" sx={{ fontSize: 12 }}>{a.quantity}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{fmtCAD(a.lineTotal)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: 13 }}>Add-ons Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13 }}>{fmtCAD(booking.addonsSubtotal)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* ── Notes ── */}
            {(booking.publicNotes || booking.privateNotes) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <SectionTitle>Notes</SectionTitle>
                <Grid container spacing={2}>
                  {booking.publicNotes && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em" }}>
                        Public Notes
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                        {booking.publicNotes}
                      </Typography>
                    </Grid>
                  )}
                  {booking.privateNotes && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "warning.main", display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em" }}>
                        Private Notes (Internal)
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                        {booking.privateNotes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Grid>

          {/* ── Right column (1/3): Financials + Payments ── */}
          <Grid item xs={12} md={4}>

            {/* ── Financial Summary (with expandable breakdown) ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <SectionTitle>Financial Summary</SectionTitle>
              <FinancialBreakdown booking={booking} />
            </Paper>

            {/* ── Security Deposit + Payments ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <SectionTitle>Payments &amp; Deposits</SectionTitle>

              {/* Security deposit */}
              {Number(booking.securityDepositAmount) > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.07em", display: "block", mb: 0.5 }}>
                    Security Deposit
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2">
                      {fmtCAD(booking.securityDepositAmount)}
                      {booking.securityDepositMethod && ` via ${booking.securityDepositMethod}`}
                    </Typography>
                    {booking.securityDepositDate && (
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(booking.securityDepositDate)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Payment list */}
              {payments.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 11 } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p: any, i: number) => (
                      <TableRow key={i} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell sx={{ fontSize: 12 }}>{p.paymentDate ? fmtDate(p.paymentDate) : "—"}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.method ?? "—"}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{fmtCAD(p.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                  No payments recorded.
                </Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Totals */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Paid to date</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">{fmtCAD(paidToDate)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">Balance owing</Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={balance > 0 ? "error.main" : "success.main"}
                >
                  {fmtCAD(Math.max(0, balance))}
                </Typography>
              </Box>
            </Paper>

            {/* ── Created / Updated ── */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle>Record Info</SectionTitle>
              <InfoRow label="Created"       value={fmtDate(booking.created_at)} />
              <InfoRow label="Last updated"  value={fmtDate(booking.updated_at)} />
            </Paper>

          </Grid>
        </Grid>

        {/* ── Delete Confirmation ── */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs">
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberIcon color="error" />
              Delete Booking?
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete <strong>{booking.bookingRef}</strong>. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button variant="outlined" className={classes.addButton} onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              className={classes.addButton}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>

      {/* Hidden print view — rendered off-screen for print/PDF */}
      {booking && (
        <Box sx={{ position: "fixed", left: -9999, top: 0, width: 800, "@media print": { position: "static", left: 0, width: "100%" } }}>
          <BookingPrintView ref={printRef} booking={booking} />
        </Box>
      )}
    </Container>
  );
};

export default BanquetBookingDetails;
